import React, { useCallback, useRef } from "react";
import {
  FileUpload,
  useUploadFileAndCreateSearchDoc,
} from "./useUploadFileAndCreateSearchDoc";
import { ChatAttachmentItem } from "@src/ui/components/ai-chat/ChatAttachment";

export interface UseAiChatComposerFileUploadProps {
  onAttachmentsChanged?: React.Dispatch<
    React.SetStateAction<ChatAttachmentItem[]>
  >;
}

export const useAiChatComposerFileUpload = ({
  onAttachmentsChanged,
}: UseAiChatComposerFileUploadProps): {
  triggerFileUpload: () => void;
  HiddenFileInput: React.ReactNode;
} => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const onUploadStarted = useCallback(
    (fileUpload: FileUpload) => {
      if (!onAttachmentsChanged) return;
      const newAttachment = { uploadingFile: fileUpload };
      onAttachmentsChanged((prev) => [...(prev ?? []), newAttachment]);
    },
    [onAttachmentsChanged],
  );

  const onUploadCompleted = useCallback(
    (fileUpload: FileUpload) => {
      if (!onAttachmentsChanged) return;
      onAttachmentsChanged((prev) =>
        prev.map((att) => {
          if (att.uploadingFile?.uuid === fileUpload.uuid) {
            return {
              ...att,
              uploadingFile: fileUpload,
              mediaFragment: fileUpload.media,
            };
          }
          return att;
        }),
      );
    },
    [onAttachmentsChanged],
  );

  const onUploadError = useCallback(
    (fileUpload: FileUpload) => {
      if (!onAttachmentsChanged) return;
      onAttachmentsChanged((prev) =>
        prev.map((att) => {
          if (att.uploadingFile?.uuid === fileUpload.uuid) {
            return {
              ...att,
              uploadingFile: fileUpload,
              mediaFragment: fileUpload.media,
            };
          }
          return att;
        }),
      );
    },
    [onAttachmentsChanged],
  );

  const { uploadFiles } = useUploadFileAndCreateSearchDoc({
    onUploadStarted,
    onUploadCompleted,
    onUploadError,
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    uploadFiles(Array.from(files));
    // Reset file input to allow selecting the same file again
    if (e.target) {
      e.target.value = "";
    }
  };

  const triggerFileUpload = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  const HiddenFileInput = (
    <input
      ref={fileInputRef}
      type="file"
      multiple
      style={{ display: "none" }}
      onChange={handleFileSelect}
    />
  );

  return {
    triggerFileUpload,
    HiddenFileInput,
  };
};
